import { Component, DestroyRef, HostListener, Signal, computed, inject, isDevMode } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';

import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LocalizePipe } from '../../localize/localize.pipe';
import { SvgIconComponent } from '../../modules/shared/svg-icon/svg-icon.component';
import { AuthService, BookingService, ChatService, FeedbackFlowService, PopupService, UtilityService, VariableService } from '../../services';
import { SearchService } from '../../services/search.service';
import { CustomCookieServiceService } from '../../services/custom-cookie-service.service';
import { PackagesFlowService } from '../../services/packages-flow.service';
import { ClassroomService } from '../../services/classroom.service';
import { FlowRouteService, FlowStorageKeys } from '../../services/flow-route.service';
import { PaymentInfoVideoDialogComponent } from '../payment-info-video-dialog/payment-info-video-dialog.component';
import { UserObj, UserStateEnum } from '../../microservice-clients/user';
import { Appointment } from '../../models';
import { AppointmentApiType } from '../../constants';
import { NavbarTopBarComponent } from './navbar-top-bar/navbar-top-bar.component';
import { NavbarClassroomBarComponent } from './navbar-classroom-bar/navbar-classroom-bar.component';
import { NavbarDropdownMenuComponent } from './navbar-dropdown-menu/navbar-dropdown-menu.component';
import { NavbarUserAvatarComponent } from './navbar-user-avatar/navbar-user-avatar.component';
import { NavbarCreditDisplayComponent } from './navbar-credit-display/navbar-credit-display.component';
import { PackageStatusComponent } from '../package-status/package-status.component';
import { SlideToggleComponent } from '../slide-toggle/slide-toggle.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    RouterModule,
    TranslateModule,
    LocalizePipe,
    SvgIconComponent,
    NavbarTopBarComponent,
    NavbarClassroomBarComponent,
    NavbarDropdownMenuComponent,
    NavbarUserAvatarComponent,
    NavbarCreditDisplayComponent,
    PackageStatusComponent,
    SlideToggleComponent
],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss']
})
export class NavbarComponent {
  private readonly destroyRef = inject(DestroyRef);

  isOpen = false;
  appointment: Appointment | null = null;
  appointmentPartner = '';
  appointmentType = '';
  toggleReady = false;
  hideForChat = false;
  isBecomeTutorPage = false;
  showTutorGuideButton = false;
  isPackageRequestFinished = false;

  isDevMode = isDevMode();

  readonly unopenedChatroomsCount: Signal<number>;
  readonly totalNotifications: Signal<number>;

  lastScrollY = 0;
  threshold = 150;
  allowHideHeader = true;

  get UserStateEnum(): typeof UserStateEnum {
    return UserStateEnum;
  }

  get showAiTutoringButton(): boolean {
    return this.authService.isStudent()
      && this.packagesFlowService.hasPaidPackage
      && String(this.authService.user.id).endsWith('0')
      && this.utilityService.isNL;
  }

  get showDesktopBuyButton(): boolean {
    return this.isPackageRequestFinished
      && this.authService.isStudent()
      && (this.authService.userDetails?.state === UserStateEnum.Introducing
        || this.authService.userDetails?.state === UserStateEnum.Tutoring)
      && (!this.packagesFlowService.totalCredits || this.packagesFlowService.totalCredits === '0')
      && !(this.packagesFlowService.pendingPackage
        && this.packagesFlowService.pendingPackage.paymentIntent.currentInstallment != '1')
      && !this.packagesFlowService.suspendedPackage;
  }

  get showDesktopCredits(): boolean {
    return this.isPackageRequestFinished
      && this.authService.isStudent()
      && !this.packagesFlowService.suspendedPackage
      && !!this.packagesFlowService.totalCredits
      && this.packagesFlowService.totalCredits !== '0'
      && (!this.packagesFlowService.pendingPackage
        || this.packagesFlowService.pendingPackage.paymentIntent.currentInstallment == '1');
  }

  get showPackageStatus(): boolean {
    return this.authService.isStudent()
      && ((!!this.packagesFlowService.pendingPackage
        && this.packagesFlowService.pendingPackage.paymentIntent.currentInstallment != '1')
        || !!this.packagesFlowService.enrollingPackageButCreditEnded
        || !!this.packagesFlowService.suspendedPackage);
  }

  get showMobileFindTutorButton(): boolean {
    return this.isPackageRequestFinished
      && this.authService.isStudent()
      && (this.authService.userDetails?.state !== UserStateEnum.Introducing
        && this.authService.userDetails?.state !== UserStateEnum.Tutoring)
      && (!this.packagesFlowService.totalCredits || this.packagesFlowService.totalCredits === '0')
      && !this.utilityService.isDesktopRes()
      && !this.utilityService.isNL;
  }

  constructor(
    public authService: AuthService,
    public chatService: ChatService,
    public utilityService: UtilityService,
    public variableService: VariableService,
    public popupService: PopupService,
    public searchService: SearchService,
    public packagesFlowService: PackagesFlowService,
    public bookingService: BookingService,
    private feedbackFlowService: FeedbackFlowService,
    private translateService: TranslateService,
    private cookieService: CustomCookieServiceService,
    private router: Router,
    private classRoomService: ClassroomService,
    private flowRouteService: FlowRouteService,
  ) {
    const rooms = toSignal(this.chatService.rooms$, { initialValue: [] });
    this.unopenedChatroomsCount = computed(() => rooms().filter(r => r.unread_messages > 0 || r.unseen).length);
    this.totalNotifications = computed(() => {
      return this.bookingService.pendingAppointmentsCount() + this.unopenedChatroomsCount();
    });
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (this.utilityService.isBrowser() && this.allowHideHeader) {
      const currentScrollY = window.scrollY;

      if (currentScrollY < this.lastScrollY || currentScrollY < this.threshold) {
        this.utilityService.isHeaderHidden = false;
        this.closeDropdown();
      } else {
        this.utilityService.isHeaderHidden = true;
        this.closeDropdown();
      }
      this.lastScrollY = currentScrollY;
    }
  }

  ngOnInit(): void {
    if (this.utilityService.isBrowser()) {
      this.lastScrollY = window.scrollY;

      this.translateService.get([
        'ROUTES.Misc_tutor_search',
        'ROUTES.Misc_become_tutor',
        'ROUTES.TutorRegistration_TeacherInfo',
        'ROUTES.Misc_profile'
      ]).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((res) => {
        const searchPageRoute = res['ROUTES.Misc_tutor_search'];
        const tutorProfileRoute = res['ROUTES.Misc_profile'];
        const becomeTutorRoute = res['ROUTES.Misc_become_tutor'];
        const becomeTutorInfoRoute = res['ROUTES.TutorRegistration_TeacherInfo'];
        this.setFlags(searchPageRoute, tutorProfileRoute);
        this.updateRegisterToTutor(becomeTutorRoute, becomeTutorInfoRoute);

        this.router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
          this.setFlags(searchPageRoute, tutorProfileRoute);
          this.updateRegisterToTutor(becomeTutorRoute, becomeTutorInfoRoute);
        });
      });

      if (this.authService.user) {
        this.initializeLoggedInUserData();
      }

      this.authService.onCurrentUserChange.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        if (this.authService.user) {
          this.initializeLoggedInUserData();
        }
      });
    }
  }

  joinClassroom(): void {
    if (!this.appointment) return;

    if ((this.isIntroductionAppointment() || this.isLessonAppointment()) && this.authService.isStudent()) {
      this.feedbackFlowService.prepareFeedback(this.appointment);
    }
    this.openClassroom();
  }

  async openBuyPackageDialog(): Promise<void> {
    await this.flowRouteService.navigateToFlow(FlowStorageKeys.packagesFlowReturnTo);
    this.utilityService.trackEvent('all_click_package_button');
  }

  openPaymentVideo(): void {
    this.popupService.openResponsiveDialog(PaymentInfoVideoDialogComponent);
  }

  updatePreference(): void {
    const user = {
      id: this.authService.userDetails?.id,
      tutorDetails: {
        acceptsNewStudents: (this.authService.userDetails?.tutorDetails._acceptsNewStudents === true) ? 'true' : 'false',
      }
    } as UserObj;
    this.authService.updateUserDetails(user).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.utilityService.trackEvent('Availability_New_Students_Menu', 'Successful', `${user.tutorDetails.acceptsNewStudents}`, this.authService.userDetails.id);
    });
  }

  redirectToStudentEditProfile(): void {
    this.translateService.get(['ROUTES.Navigation_edit_profile_student']).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(link => {
      this.router.navigate([link['ROUTES.Navigation_edit_profile_student']]);
    });
  }

  async openRegister(): Promise<void> {
    if (this.cookieService.get('block-register')) {
      this.popupService.openInfoDialog({ title: 'texts.RegisterBlockBookLimitTitle', description: 'texts.RegisterBlockBookLimitDescription' });
      return;
    } else if (this.isBecomeTutorPage) {
      const dialog = await this.popupService.showRegisterModalTutor();
      this.popupService.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => dialog.close());
    } else {
      const dialog = await this.popupService.openStudentRegistration('direct');
      this.popupService.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => dialog.close());
    }
  }

  async openLogin(): Promise<void> {
    const dialog = await this.popupService.showLoginModal();
    this.popupService.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => dialog.close());
  }

  logOut(): void {
    this.packagesFlowService.clearUserPackages();
    this.authService.logout();
  }

  toggleOnOff(): void {
    this.isOpen = !this.isOpen;
  }

  openDropdown(): void {
    this.isOpen = true;
  }

  closeDropdown(): void {
    this.isOpen = false;
  }

  showDropdown(): void {
    this.isOpen = true;
  }

  hideDropdown(event: MouseEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget && !relatedTarget.closest('.dropdown')) {
      this.isOpen = false;
    }
  }

  keepDropdownOpen(): void {
    this.isOpen = true;
  }

  private initializeLoggedInUserData(): void {
    this.getAppointment();
    if (this.authService.isStudent()) {
      this.packagesFlowService.getUserPackages(this.authService.user.id).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.isPackageRequestFinished = true;
      });
    }
    if (this.authService.isTeacher()) {
      this.checkTutorGuide();
    }
  }

  private checkTutorGuide(): void {
    const createdAt = new Date(this.authService.userDetails?.createdAt);
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    this.showTutorGuideButton = createdAt >= twoWeeksAgo;
  }

  private getAppointment(): void {
    this.bookingService.getUpComingAppointment().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((resp) => {
      if (resp) {
        this.appointment = resp;
        this.appointmentPartner = this.authService.isStudent() ? resp.tutor.forename : resp.student.forename;
        this.appointmentType = resp.type;
        if (!resp.online) {
          this.appointment._googleMapsLink = `https://www.google.com/maps/?q=${resp.locationLatitude},${resp.locationLongitude}`;
        }
      }
    });
  }

  private isIntroductionAppointment(): boolean {
    return this.appointment?.type === AppointmentApiType.introduction;
  }

  private isLessonAppointment(): boolean {
    return this.appointment?.type === AppointmentApiType.lesson;
  }

  private openClassroom(): void {
    if (!this.appointment) return;
    const isIntro = this.appointment.type === AppointmentApiType.introduction;
    const partnerId = this.authService.isTeacher() ? this.appointment.student.id : this.appointment.tutor.id;
    this.classRoomService.joinClassroom(partnerId, isIntro);
  }

  private updateRegisterToTutor(becomeTutorRoute: string, becomeTutorInfoRoute: string): void {
    this.isBecomeTutorPage = this.router.url.includes(becomeTutorRoute) || this.router.url.includes(becomeTutorInfoRoute);
  }

  private setFlags(searchPageRoute: string, tutorProfileRoute?: string): void {
    this.hideForChat = this.router.url.includes('chat');
    setTimeout(() => {
      this.toggleReady = !this.hideForChat;
    });
    if (this.router.url.includes(searchPageRoute) || (tutorProfileRoute && this.router.url.includes(tutorProfileRoute))) {
      this.allowHideHeader = false;
    } else {
      this.allowHideHeader = true;
    }
  }
}
